import { reactiveComputed } from '@vueuse/core';
import {
  computed, ComputedRef, inject, provide, toRefs,
} from 'vue';

import { IContractRecord } from '@/services/requests/contracts/Contracts.types';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { useOnboardingAdminSoleTraderStore, useOnboardingUserProfileStore, usePersonalOnboardingStore } from '@/stores';

export interface CustomerProfileProvidedData {
  isApplicationUnblockedVerified: boolean;
  isApplicationEditable: boolean;
  contract?: Partial<IContractRecord>;
}

interface StoreData {
  applicationStatus?: Onboarding.ApplicationStatus;
  blocked: boolean;
  contract?: Partial<IContractRecord>;
}

function getData(type: Onboarding.ApplicationType): ComputedRef<StoreData> {
  if (type === 'CORPORATE') {
    const store = useOnboardingUserProfileStore();
    return computed(() => ({
      applicationStatus: store.applicationStatus,
      blocked: store.blocked,
      contract: store.contract,
    }));
  }
  if (type === 'PERSONAL') {
    const { applicationStatus, blocked, contract } = usePersonalOnboardingStore();
    return computed(() => ({
      applicationStatus: applicationStatus?.value,
      blocked: blocked.value,
      contract: contract?.value,
    }));
  }
  const { applicationStatus, blocked, contract } = useOnboardingAdminSoleTraderStore();
  return computed(() => ({
    applicationStatus: applicationStatus?.value,
    blocked: blocked.value,
    contract: contract?.value,
  }));
}

export function useCustomerProfileProvide(type: Onboarding.ApplicationType) {
  const editableStatuses: Onboarding.ApplicationStatus[] = ['NON_VERIFIED', 'VERIFIED', 'IN_PROGRESS', 'MANUAL'];
  const data = getData(type);

  const customerProvidedData = reactiveComputed<CustomerProfileProvidedData>(() => ({
    contract: data.value.contract,
    isApplicationUnblockedVerified: data.value.applicationStatus === 'VERIFIED' && !data.value.blocked,
    isApplicationEditable: data.value.applicationStatus !== undefined
      ? editableStatuses.includes(data.value.applicationStatus)
      : false,
  }));

  provide('application', customerProvidedData);
}

export function useCustomerProfileInject() {
  return toRefs(inject<CustomerProfileProvidedData>('application')!);
}
